/**
 * A quick look at an item without leaving the page. What's looked at is in the URL (`?peek=<id>`: back closes it, a
 * reload keeps it); where it sits is its placement, which its frame lays out.
 */
import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { folderHref, isFolderId } from "./folders.ts";
import { post, provides } from "./native.ts";
import { spaceUrl } from "./paths.ts";
import { carry, fullLocation, navigate, overlay, spacePath } from "./router.ts";
import { store, stored } from "./storage.ts";

const PARAM = "peek";
// Over the page: opening, swapping or closing a look changes nothing the page reads.
overlay(PARAM);
const SPACES = /\s+/u;

export type Side = "left" | "right";

/**
 * Where the look sits. Docked: full height, on one side; over the app (the page's other side left in view), or
 * `pinned` beside it (the app makes room: a split). Other ways (a minimized rail, a window) would be other modes,
 * laid out by the frame.
 */
interface Placement {
  mode: "docked";
  side: Side;
  pinned: boolean;
}

const PINNED = "revisa.peek.pinned";

/** Pinned stays pinned in this browser: the next look opens beside the page too. */
const pinnedKept = (): boolean => stored(PINNED) === "1";

/** The side a look was opened on, kept with its history entry: a reload (or going back to it) opens it there again. */
const sideKept = (): Side => {
  const kept = (globalThis.history?.state as { peek?: Side } | null)?.peek;
  return kept === "left" ? "left" : "right";
};

export const placement = signal<Placement>({ mode: "docked", side: sideKept(), pinned: pinnedKept() });

/** The look opened a history entry of its own (closing it goes back), and what it was opened from (focus returns). */
let pushed = false;
let opener: HTMLElement | null = null;

/** The item looked at, if any. */
export function peeked(): string | null {
  return fullLocation.value.searchParams.get(PARAM);
}

/** The cell of a grid with more than one column holding `el`: the page's column it's in. */
function columnOf(el: HTMLElement): HTMLElement | null {
  for (let at: HTMLElement | null = el; at?.parentElement; at = at.parentElement) {
    const grid = getComputedStyle(at.parentElement);
    if (grid.display === "grid" && grid.gridTemplateColumns.trim().split(SPACES).length > 1) {
      return at;
    }
  }
  return null;
}

/**
 * The side a look of what `from` links to opens on (see `peek`): where it is when it stays, else away from `from`'s
 * column, which stays in view.
 */
export function sideFor(from: HTMLElement): Side {
  const open = fullLocation.value.searchParams.has(PARAM);
  if (from.closest("[data-peek]") || (open && placement.value.pinned)) {
    return placement.value.side;
  }
  // The window's middle, not the page's (the sidebar shifts that): the look takes half the window.
  const box = (columnOf(from) ?? from).getBoundingClientRect();
  return box.left + box.width / 2 > globalThis.innerWidth / 2 ? "left" : "right";
}

/** Whether looks are the app's (its sheet, the item's page in it, at any width), not this page's. */
const appLooks = (): boolean => provides("peek");

/**
 * Item (or folder) `id` looked at in the app's sheet (`side`: where a panel at the side opens, away from what opened
 * it; none: where it is); whether it is (not: the app has none, or won't).
 */
async function inApp(id: string, side?: Side): Promise<boolean> {
  const space = spacePath(fullLocation.value.pathname)?.space;
  if (!(space && appLooks())) {
    return false;
  }
  const path = isFolderId(id) ? folderHref({ id: space }, id) : spaceUrl({ id: space }, `/items/${id}`);
  return (await post("peek", { path, side: side ?? null }).catch(() => false)) === true;
}

/**
 * Looks at item `id`, from `from` (a link to it). Opening, it takes the side away from where `from` is (that column
 * stays in view), pinned or not. Already open, it stays where it is: from inside the look, or beside the page pinned
 * (the page moving across under the pointer would be too much); over it, it moves away from `from` again. Where looks
 * are the app's, there instead (this page's own if it won't).
 */
export function peek(id: string, from: HTMLElement): void {
  if (!appLooks()) {
    look(id, from);
    return;
  }
  inApp(id, sideFor(from))
    .then((shown) => {
      if (!shown) {
        look(id, from);
      }
    })
    .catch(() => look(id, from));
}

/**
 * A look in the address (`?peek=`: a link to it, a reload) where looks are the app's: shown there, and gone from the
 * address. Whether it's the app's to show (false: this page shows it; also once the app wouldn't).
 */
export function useHandOver(id: string | null): boolean {
  const [refused, setRefused] = useState<string | null>(null);
  const hand = id !== null && id !== refused && appLooks();
  useEffect(() => {
    if (!(hand && id)) {
      return;
    }
    inApp(id)
      .then((shown) => {
        if (!shown) {
          setRefused(id);
          return;
        }
        const u = new URL(fullLocation.value.href);
        u.searchParams.delete(PARAM);
        navigate(`${u.pathname}${u.search}`, true);
      })
      .catch(() => setRefused(id));
  }, [hand, id]);
  return hand;
}

/** Looks at item `id` here, from `from` (see `peek`). */
function look(id: string, from: HTMLElement): void {
  const u = new URL(fullLocation.value.href);
  const open = u.searchParams.has(PARAM);
  const inside = from.closest("[data-peek]") !== null;
  placement.value = { ...placement.value, side: sideFor(from) };
  if (!inside) {
    opener = from;
  }
  u.searchParams.set(PARAM, id);
  // Another item in an open look replaces it: back closes the look, not each item looked at.
  navigate(`${u.pathname}${u.search}`, open, { peek: placement.value.side });
  pushed ||= !open;
}

/** Closing the look: not carried along to where that goes. */
let closing = false;

// Pinned, the look goes along to other pages of the space (and other tabs of one) (it's beside them, not part of one), but not to its own
// item's page (Open: the item is there itself), nor out of the space. Closing it then leaves the page as it is.
carry((to, from) => {
  const id = from.searchParams.get(PARAM);
  if (
    !(id && placement.value.pinned) ||
    closing ||
    to.searchParams.has(PARAM) ||
    spacePath(to.pathname)?.space !== spacePath(from.pathname)?.space ||
    to.pathname.endsWith(`/items/${id}`)
  ) {
    return;
  }
  to.searchParams.set(PARAM, id);
  pushed = false;
  return { peek: placement.value.side };
});

/** The look is sliding out to come back on the other side. */
export const moving = signal(false);
/** Its slide out (the frame's `animate-slide-out`). */
const MOVE_MS = 250;

/**
 * The look goes to the other side (kept with its history entry, as when opened): over the page, out where it is, then
 * in there; pinned, across (the page crossing it the other way).
 */
export function otherSide(): void {
  if (moving.value) {
    return;
  }
  const flip = () => {
    placement.value = { ...placement.value, side: placement.value.side === "left" ? "right" : "left" };
    navigate(`${fullLocation.value.pathname}${fullLocation.value.search}`, true, { peek: placement.value.side });
    moving.value = false;
  };
  // Pinned, the page and the look cross each other (the frame moves it across, the page's room moves too); where
  // motion is reduced, at once.
  if (placement.value.pinned || globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    flip();
    return;
  }
  moving.value = true;
  setTimeout(flip, MOVE_MS);
}

/** Pins the look beside the page (or unpins it, back over the page), remembered for the next. */
export function pin(on: boolean): void {
  placement.value = { ...placement.value, pinned: on };
  store(PINNED, on ? "1" : "0");
}

/** The look is open, pinned: the app makes room for it on that side (null: it doesn't). */
export function splitSide(): Side | null {
  return peeked() !== null && placement.value.pinned ? placement.value.side : null;
}

/** Closes the look (back, when it opened an entry), focus back on what opened it. */
export function closePeek(): void {
  if (pushed) {
    pushed = false;
    globalThis.history.back();
  } else {
    const u = new URL(fullLocation.value.href);
    u.searchParams.delete(PARAM);
    closing = true;
    navigate(`${u.pathname}${u.search}`, true);
    closing = false;
  }
  if (opener?.isConnected) {
    opener.focus();
  }
  opener = null;
}
